import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Check, ExternalLink, LoaderCircle, RefreshCw, Users, Zap } from 'lucide-react';
import { useQueryClient } from 'react-query';

import { fetchPaddleClientToken } from '@/api/billing/fetchPaddleClientToken';
import { fetchPaddleCustomerPortalUrl } from '@/api/billing/fetchPaddleCustomerPortal';
import { fetchSyncPaddleTenantBilling } from '@/api/billing/fetchSyncPaddleTenantBilling';
import { usePaddleCheckoutPlans } from '@/api/billing/usePaddleCheckoutPlans';
import { useGetAccountDetails } from '@/api/settings/useGetAccountDetails';
import { useQuotaUsage } from '@/api/settings/useQuotaUsage';
import { Button } from '@/component-library/primitives/Button/Button';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { openPaddleSubscriptionCheckout } from '@/lib/paddle/openPaddleSubscriptionCheckout';
import queryKeys from '@/modules/shared/query/queryKeys';
import type { CompanyAccountDetails } from '@/models/CompanyAccountDetails';

type BillingUserShape = {
  signInUserSession?: {
    idToken?: {
      payload?: Record<string, unknown>;
    };
  };
  attributes?: {
    custom?: { tenantId?: string };
    email?: string;
  };
} | null;

function formatLimit(n: number): string {
  if (n === -1) return 'Unlimited';
  return n.toLocaleString();
}

/** `/tenants` may be missing locally; billing still needs a tenant id for Paddle custom_data. */
function resolveTenantIdForBilling(account: CompanyAccountDetails | undefined, user: BillingUserShape): string {
  const fromAccount = account?.tenantId?.trim();
  if (fromAccount) return fromAccount;
  const raw = user?.signInUserSession?.idToken?.payload?.['custom:tenantId'];
  const fromToken = typeof raw === 'string' ? raw.trim() : '';
  if (fromToken) return fromToken;
  return user?.attributes?.custom?.tenantId?.trim() ?? '';
}

function resolveBillingEmail(account: CompanyAccountDetails | undefined, user: BillingUserShape): string {
  const fromAccount = (account?.invoiceEmail || account?.legalEmail || account?.technicalEmail || '').trim();
  if (fromAccount) return fromAccount;
  const fromToken = user?.signInUserSession?.idToken?.payload?.email;
  if (typeof fromToken === 'string' && fromToken.trim()) return fromToken.trim();
  const fromAttrs = user?.attributes?.email;
  return (fromAttrs && String(fromAttrs).trim()) || '';
}

const SettingsSubscriptionPanel = () => {
  const { t } = (useTranslation as unknown as () => { t: (key: string) => string })();
  const { user } = useAuth();
  const {
    data: account,
    isLoading: accountLoading,
    isError: accountDetailsError,
    error: accountDetailsQueryError,
  } = useGetAccountDetails();
  const { data: plans = [], isLoading: plansLoading, isError: plansError, error: plansQueryError } =
    usePaddleCheckoutPlans();
  const { data: quota, isLoading: quotaLoading } = useQuotaUsage();
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const queryClient = useQueryClient();

  const currentPackageId = (quota?.packageId ?? '').trim().toLowerCase() || null;

  const upgradePlans = useMemo(() => {
    if (!currentPackageId) return plans;
    return plans.filter((p) => (p.id ?? '').trim().toLowerCase() !== currentPackageId);
  }, [plans, currentPackageId]);

  const singleUpgradePlan = upgradePlans.length === 1;
  useEffect(() => {
    if (upgradePlans.length === 0) return;
    setSelectedPriceId((prev) => {
      const stillValid = prev && upgradePlans.some((p) => p.paddlePriceId === prev);
      if (stillValid) return prev;
      const first = upgradePlans[0]?.paddlePriceId?.trim();
      return first || null;
    });
  }, [upgradePlans]);

  const loading = accountLoading || plansLoading || quotaLoading;
  const jwt = user?.signInUserSession?.idToken?.jwtToken;

  const syncFromPaddle = async () => {
    setError('');
    setSuccessMessage('');
    setBusy(true);
    try {
      const result = await fetchSyncPaddleTenantBilling(jwt);
      await queryClient.invalidateQueries([queryKeys.companyAccount]);
      await queryClient.invalidateQueries([queryKeys.quotaUsage]);
      setSuccessMessage(t('SettingsPage.subscriptionSyncPaddleSuccess').replace('{{plan}}', result.packageName));
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        t('SettingsPage.subscriptionSyncPaddleError');
      setError(typeof msg === 'string' ? msg : t('SettingsPage.subscriptionSyncPaddleError'));
    } finally {
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setError('');
    setSuccessMessage('');
    setBusy(true);
    try {
      const url = await fetchPaddleCustomerPortalUrl(jwt);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        t('SettingsPage.subscriptionPortalError');
      setError(typeof msg === 'string' ? msg : t('SettingsPage.subscriptionPortalError'));
    } finally {
      setBusy(false);
    }
  };

  const openUpgradeCheckout = async () => {
    if (!selectedPriceId) {
      setError(t('SettingsPage.subscriptionSelectPlan'));
      return;
    }
    const tenantId = resolveTenantIdForBilling(account, user as BillingUserShape);
    if (!tenantId) {
      setError(t('SettingsPage.subscriptionCheckoutMissingTenant'));
      return;
    }
    const email = resolveBillingEmail(account, user as BillingUserShape);
    if (!email) {
      setError(t('SettingsPage.subscriptionMissingEmail'));
      return;
    }
    setError('');
    setSuccessMessage('');
    setBusy(true);
    try {
      const { token, sandbox } = await fetchPaddleClientToken(jwt);
      const origin = window.location.origin;
      const successUrl = `${origin}/settings`;
      await openPaddleSubscriptionCheckout({
        clientToken: token,
        sandbox,
        priceId: selectedPriceId,
        customerEmail: email,
        tenantId,
        successUrl,
      });
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } } };
      const fromApi = ax?.response?.data?.error;
      const fromErr = e instanceof Error ? e.message : '';
      const msg =
        (typeof fromApi === 'string' && fromApi) ||
        (fromErr && String(fromErr).trim()) ||
        t('SettingsPage.subscriptionCheckoutError');
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-custom-darkSilver">
        <LoaderCircle className="h-5 w-5 animate-spin text-custom-blue" />
        {t('SettingsPage.subscriptionLoading')}
      </div>
    );
  }

  if (plansError) {
    const detail = axios.isAxiosError(plansQueryError)
      ? ((plansQueryError.response?.data as { error?: string } | undefined)?.error ??
          (plansQueryError.response?.status
            ? `HTTP ${plansQueryError.response.status}`
            : plansQueryError.message))
      : '';
    return (
      <div className="max-w-2xl space-y-2 text-sm text-custom-darkSilver">
        <p className="text-custom-red">{t('SettingsPage.subscriptionPlansLoadError')}</p>
        {detail ? <p className="break-all font-mono text-xs text-custom-darkSilver">{detail}</p> : null}
      </div>
    );
  }

  if (!plans.length) {
    return (
      <div className="max-w-2xl space-y-3 text-sm text-custom-darkSilver">
        <p>{t('SettingsPage.subscriptionNoPlans')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h2 className="text-lg font-medium text-custom-black">{t('SettingsPage.subscriptionTitle')}</h2>
        <p className="mt-1 text-sm text-custom-darkSilver">{t('SettingsPage.subscriptionSubtitle')}</p>
      </div>

      {accountDetailsError && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
          {t('SettingsPage.subscriptionAccountLoadWarning')}
          {axios.isAxiosError(accountDetailsQueryError) && accountDetailsQueryError.response?.status ? (
            <span className="ml-1 font-mono text-xs">({accountDetailsQueryError.response.status})</span>
          ) : null}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-custom-red/30 bg-custom-red/10 p-3 text-sm text-custom-red">{error}</div>
      )}

      {successMessage && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-950">{successMessage}</div>
      )}

      <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50/80 p-4">
        <h3 className="text-sm font-medium text-custom-black">{t('SettingsPage.subscriptionPortalSectionTitle')}</h3>
        <div className="flex flex-wrap gap-3">
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={() => void openPortal()}
            className="inline-flex items-center gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            {t('SettingsPage.subscriptionManageButton')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={() => void syncFromPaddle()}
            className="inline-flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            {t('SettingsPage.subscriptionSyncPaddleButton')}
          </Button>
        </div>
        <p className="text-xs text-custom-darkSilver">{t('SettingsPage.subscriptionManageHint')}</p>
        <p className="text-xs text-custom-darkSilver">{t('SettingsPage.subscriptionSyncPaddleHint')}</p>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-medium text-custom-black">
          {singleUpgradePlan
            ? t('SettingsPage.subscriptionCheckoutTitleSingle')
            : t('SettingsPage.subscriptionUpgradeTitle')}
        </h3>
        {upgradePlans.length === 0 ? (
          <p className="text-sm text-custom-darkSilver">{t('SettingsPage.subscriptionAlreadyOnCheckoutPlan')}</p>
        ) : (
          <>
            <div
              className={
                singleUpgradePlan ? 'max-w-md space-y-4' : 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3'
              }
            >
              {upgradePlans.map((plan) => {
                const selected = selectedPriceId === plan.paddlePriceId;
                return (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => setSelectedPriceId(plan.paddlePriceId)}
                    className={`relative flex flex-col rounded-xl border-2 p-5 text-left shadow-sm transition-all hover:shadow-md ${
                      selected
                        ? 'border-custom-blue bg-custom-blue/5 ring-2 ring-custom-blue/30'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    {selected && (
                      <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-custom-blue">
                        <Check className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
                      </span>
                    )}
                    <span className="text-sm font-medium uppercase tracking-wide text-custom-darkSilver">{plan.name}</span>
                    {plan.description && (
                      <span className="mt-2 text-sm text-custom-darkSilver">{plan.description}</span>
                    )}
                    <ul className="mt-4 space-y-2 text-sm text-custom-black">
                      <li className="flex items-center gap-2">
                        <Users className="h-4 w-4 shrink-0 text-custom-blue" />
                        <span>
                          {t('SignUpPage.planUsers')}: {formatLimit(plan.numberOfUsers)}
                        </span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Zap className="h-4 w-4 shrink-0 text-custom-blue" />
                        <span>
                          {t('SignUpPage.planEvents')}: {formatLimit(plan.maxEventsPerMonth)}
                        </span>
                      </li>
                    </ul>
                  </button>
                );
              })}
            </div>
            <Button
              type="button"
              className="mt-2 bg-custom-blue hover:bg-custom-darkBlue text-white"
              disabled={busy || !selectedPriceId}
              onClick={() => void openUpgradeCheckout()}
            >
              {singleUpgradePlan
                ? t('SettingsPage.subscriptionOpenCheckoutSingle')
                : t('SettingsPage.subscriptionUpgradeCheckout')}
              {busy && <LoaderCircle className="ml-2 inline h-4 w-4 animate-spin" />}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default SettingsSubscriptionPanel;
